import React, { useMemo } from 'react';
import moment from 'moment';
import { SortDirection } from '@mui/material';
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Check } from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StyledLink } from '../Components';
import { ColWidths, TableCellEllipsis, TableCellSort, compareFn } from '../components/TableComponents';
import { SearchField } from '../components/SearchField';
import { htmlIsEmpty } from '../components/richText';
import LoadingSpinner from '../LoadingSpinner';
import { formatDateOnly } from '../Formatters';
import { StyledPaper } from '../theme';
import { useModal } from '../dialogs/useModal';
import { GetTermsQuery } from '../__generated__/graphql';
import { Unpacked } from '../graphQLTypes/types';
import { GET_ALL } from './dictionary.graphql';
import { TermDialog, TermDialogInput } from './TermDialog';
import { useCanEditDictionary } from './useCanEditDictionary';
import { Hit as SearchHit, searchTerms } from './search';

type Term = NonNullable<Unpacked<GetTermsQuery['terms']>>;
type CategoryOption = { id: string; title?: string | null };

type Hit = SearchHit<Term>;

/** The list's search, filters and sorting, kept in the URL so they survive navigating to a term and back. */
const useListParams = () => {
  const [params, setParams] = useSearchParams();
  const set = (values: Record<string, string | null | undefined>) =>
    setParams(
      (prev) => {
        Object.entries(values).forEach(([key, value]) => (value ? prev.set(key, value) : prev.delete(key)));
        return prev;
      },
      { replace: true },
    );
  return [params, set] as const;
};

export function Overview() {
  const { data } = useQuery(GET_ALL, { fetchPolicy: 'cache-and-network' });
  const canEdit = useCanEditDictionary();
  const navigate = useNavigate();
  const addModal = useModal<TermDialogInput, string | undefined>({ data: {} });

  const [params, setParam] = useListParams();
  const searchTerm = params.get('q') ?? '';
  const categoryId = params.get('fagomrade');
  const onlyWithDefinition = params.has('definisjon');
  const onlyWithNotes = params.has('merknad');

  const terms = useMemo(() => (data?.terms ?? []).filter((t): t is Term => !!t), [data]);

  const categories = useMemo(() => {
    const byId = new Map<string, CategoryOption>();
    terms.flatMap((t) => t.categories ?? []).forEach((c) => c && byId.set(c.id, c));
    return [...byId.values()].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
  }, [terms]);
  const category = categories.find((c) => c.id === categoryId) ?? null;

  const hits = useMemo(
    () =>
      searchTerms(
        terms
          .filter((t) => !categoryId || t.categories?.some((c) => c?.id === categoryId))
          .filter((t) => !onlyWithDefinition || !htmlIsEmpty(t.definition))
          .filter((t) => !onlyWithNotes || !htmlIsEmpty(t.notes)),
        searchTerm,
      ),
    [terms, searchTerm, categoryId, onlyWithDefinition, onlyWithNotes],
  );

  const addTerm = async () => {
    const id = await addModal.open({});
    if (id) navigate(id);
  };

  return (
    <StyledPaper>
      <Grid container direction="row">
        <Grid size={{ xs: 12 }}>
          <Typography variant="h5">
            Ordliste
            {canEdit && (
              <IconButton onClick={addTerm} title="Legg til nytt begrep" size="large">
                <Add />
              </IconButton>
            )}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <SearchField startSearch={searchTerm} filter={(q) => setParam({ q })} />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1, mb: 1 }}>
            <Autocomplete
              sx={{ minWidth: 250 }}
              size="small"
              options={categories}
              value={category}
              onChange={(_e, v) => setParam({ fagomrade: v?.id })}
              getOptionLabel={(o) => o.title ?? ''}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Fagområde" />}
            />
            <FormControlLabel
              control={<Checkbox checked={onlyWithDefinition} onChange={(e) => setParam({ definisjon: e.target.checked ? '1' : null })} />}
              label="Har definisjon"
            />
            <FormControlLabel
              control={<Checkbox checked={onlyWithNotes} onChange={(e) => setParam({ merknad: e.target.checked ? '1' : null })} />}
              label="Har merknad om bruk"
            />
          </Stack>
          {!data && <LoadingSpinner />}
          {data && <TermTable hits={hits} />}
        </Grid>
      </Grid>
      {addModal.isOpen && <TermDialog modal={addModal} />}
    </StyledPaper>
  );
}

type SortKey = 'preferred' | 'synonyms' | 'categories' | 'definition' | 'notes' | 'updatedAt';

const sortValue: Record<SortKey, (t: Term) => unknown> = {
  preferred: (t) => t.preferred ?? '',
  synonyms: (t) => t.synonyms?.join(', ') ?? '',
  categories: (t) => t.categories?.map((c) => c?.title).join(', ') ?? '',
  definition: (t) => !htmlIsEmpty(t.definition),
  notes: (t) => !htmlIsEmpty(t.notes),
  updatedAt: (t) => moment(t.updatedAt),
};

function TermTable({ hits }: { hits: Hit[] }) {
  const [params, setParam] = useListParams();
  const sortBy = params.get('sort') as SortKey | null;
  const sortDirection: SortDirection = params.get('dir') === 'desc' ? 'desc' : 'asc';

  const sorted = useMemo(
    () => (sortBy ? [...hits].sort(compareFn((h: Hit) => sortValue[sortBy](h.term), sortDirection)) : hits),
    [hits, sortBy, sortDirection],
  );

  if (hits.length === 0) return <Typography sx={{ mt: 2 }}>Ingen begreper</Typography>;

  const header = (key: SortKey, label: string) => (
    <TableCellSort
      active={sortBy === key}
      sortDirection={sortBy === key ? sortDirection : false}
      onClick={() => {
        setParam({ sort: key, dir: sortBy === key && sortDirection === 'asc' ? 'desc' : null });
      }}>
      {label}
    </TableCellSort>
  );

  return (
    <Table size="small" sx={{ position: 'relative' }}>
      <ColWidths widths={[null, null, 90, 130, null, 120]}></ColWidths>
      <TableHead>
        <TableRow>
          {header('preferred', 'Begrep (vi velger)')}
          {header('synonyms', 'Alternativ (vi velger ikke)')}
          {header('definition', 'Definisjon')}
          {header('notes', 'Merknad om bruk')}
          {header('categories', 'Fagområder')}
          {header('updatedAt', 'Sist oppdatert')}
        </TableRow>
      </TableHead>
      <TableBody>
        {sorted.map(({ term }) => (
          <TableRow key={term.id}>
            <TableCell>
              <StyledLink to={term.id}>{term.preferred}</StyledLink>
            </TableCell>
            <TableCell>{term.synonyms?.join(', ')}</TableCell>
            <TableCellEllipsis>
              {!htmlIsEmpty(term.definition) && <Check fontSize="small" color="success" titleAccess="Har definisjon" />}
            </TableCellEllipsis>
            <TableCellEllipsis>
              {!htmlIsEmpty(term.notes) && <Check fontSize="small" color="success" titleAccess="Har merknad om bruk" />}
            </TableCellEllipsis>
            <TableCellEllipsis>{term.categories?.map((c) => c?.title).join(', ')}</TableCellEllipsis>
            <TableCellEllipsis title={term.updatedBy?.name ?? undefined}>{formatDateOnly(term.updatedAt)}</TableCellEllipsis>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
